import React, { useEffect, useState } from "react";
import {
  Button,
  ColorPicker,
  Divider,
  Flex,
  Input,
  Tooltip,
  Upload,
  message,
  theme,
} from "antd";
import type { InputProps } from "antd";
import { PictureOutlined } from "@ant-design/icons";
import type { CatalogAttribute } from "./types";

/**
 * Los atributos cuyos valores llevan imagen (xcons-docs#490, Figma de Datos principales y
 * Detalles adicionales): Marca, Color, Tamaño y Modelo. Además cualquier otro cuyas opciones
 * ya traigan una, así que uno nuevo con swatches la pide solo.
 */
const CON_IMAGEN = new Set(["manufacturer", "vv_color", "vv_size", "vv_model"]);

/**
 * **Color se carga con un selector de color**, además de poder usar una imagen (xcons-docs#490):
 * Magento guarda el swatch de un color como hex o como imagen, y elegir el tono es más rápido
 * que subir una foto. Marca, Tamaño y Modelo siguen con imagen.
 */
export const llevaColor = (
  attribute: Pick<CatalogAttribute, "code"> & { label?: string | null },
) =>
  // Magento tiene dos: `vv_color` y el `color` nativo, que usan otros conjuntos. Con sólo
  // `vv_color`, crear un valor de Color en esos conjuntos no ofrecía la muestra (xcons-docs#499).
  /(^|_)colou?r$/i.test(attribute.code) || /^colou?r$/i.test(attribute.label?.trim() ?? "");

export const llevaImagen = (
  attribute: Pick<CatalogAttribute, "code" | "options"> & { label?: string | null },
) =>
  CON_IMAGEN.has(attribute.code) ||
  llevaColor(attribute) ||
  /^(marca|brand)$/i.test(attribute.label?.trim() ?? "") ||
  !!attribute.options?.some((o) => o.image);

/** El tope de la imagen de un valor: es un logo o una muestra, no una foto de producto. */
const IMAGEN_MAX_MB = 2;

/**
 * **El valor de un atributo al crearlo** (xcons-docs#490, Figma `Form Item / Vertical/image`):
 * la miniatura a la izquierda y el nombre del valor al lado. Lo de la miniatura no se sube
 * acá: viaja con el alta del valor.
 *
 * Con `conColor` la miniatura abre el selector de color, con "Usar una imagen" abajo; si no,
 * abre directo el selector de archivos. Sin `conImagen` es el campo del nombre solo.
 */
export const CampoDeValor: React.FC<
  Omit<InputProps, "value" | "onChange" | "color"> & {
    value?: string;
    onChange?: (v: string) => void;
    conImagen?: boolean;
    conColor?: boolean;
    imagen: File | null;
    onImagen: (file: File | null) => void;
    color?: string | null;
    onColor?: (hex: string | null) => void;
  }
> = ({
  value,
  onChange,
  conImagen,
  conColor,
  imagen,
  onImagen,
  color,
  onColor,
  ...resto
}) => {
  const { token } = theme.useToken();
  const [vista, setVista] = useState<string | null>(null);

  useEffect(() => {
    if (!imagen) return setVista(null);
    const url = URL.createObjectURL(imagen);
    setVista(url);
    return () => URL.revokeObjectURL(url);
  }, [imagen]);

  const input = (
    <Input
      {...resto}
      value={value}
      onChange={(e) => onChange?.(e.target.value)}
      style={{ flex: 1, minWidth: 0, ...resto.style }}
    />
  );
  if (!conImagen && !conColor) return input;

  /** Una imagen elegida reemplaza al color, y al revés: el valor tiene una sola muestra. */
  const elegirImagen = (file: File) => {
    if (file.size > IMAGEN_MAX_MB * 1024 * 1024) {
      message.error(`La imagen pesa más de ${IMAGEN_MAX_MB} MB`);
    } else {
      onImagen(file);
      onColor?.(null);
    }
    return false;
  };

  const tieneMuestra = !!imagen || !!color;
  // **Un `div` y no `Flex`**: antd esconde un `Flex` vacío (`.ant-flex:empty`), y con un color
  // elegido la miniatura no tiene hijos —el color es el fondo—, así que desaparecía y el input
  // ocupaba su lugar.
  const miniatura = (
    <div
      role="button"
      aria-label={tieneMuestra ? "Cambiar la muestra" : "Cargar la muestra"}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 32,
        height: 32,
        flexShrink: 0,
        cursor: "pointer",
        border: `1px ${tieneMuestra ? "solid" : "dashed"} ${token.colorBorder}`,
        borderRadius: token.borderRadiusSM,
        background: color ?? token.colorFillQuaternary,
        overflow: "hidden",
      }}
    >
      {vista ? (
        <img src={vista} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
      ) : color ? null : (
        <PictureOutlined style={{ color: token.colorTextQuaternary }} />
      )}
    </div>
  );

  const subirImagen = (hijo: React.ReactNode) => (
    <Upload accept="image/*" showUploadList={false} beforeUpload={elegirImagen}>
      {hijo}
    </Upload>
  );

  return (
    <Flex align="center" gap={4}>
      {conColor ? (
        <ColorPicker
          value={color ?? undefined}
          disabledAlpha
          onChangeComplete={(c) => {
            onColor?.(c.toHexString());
            onImagen(null);
          }}
          panelRender={(panel) => (
            <>
              {panel}
              <Divider style={{ margin: "8px 0" }} />
              {subirImagen(
                <Button size="small" icon={<PictureOutlined />} block>
                  Usar una imagen
                </Button>,
              )}
            </>
          )}
        >
          <Tooltip title="Elegir el color o una imagen">{miniatura}</Tooltip>
        </ColorPicker>
      ) : (
        subirImagen(
          <Tooltip title={imagen ? "Cambiar la imagen" : "Cargar la imagen"}>
            {miniatura}
          </Tooltip>,
        )
      )}
      {input}
    </Flex>
  );
};

/**
 * El cuerpo del alta de un valor: JSON con la etiqueta (y el color, si tiene), o multipart si
 * lleva imagen. Un valor sin muestra sigue viajando como hasta ahora; la muestra espera a
 * xcons-docs#491.
 */
export const cuerpoDelValor = (
  label: string,
  imagen: File | null,
  color: string | null = null,
) => {
  if (!imagen) return { body: color ? { label, color } : { label }, config: undefined };
  const body = new FormData();
  body.append("label", label);
  body.append("image", imagen);
  return { body, config: { headers: { "Content-Type": "multipart/form-data" } } };
};
